// EventAdapter.tsx
import { useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { routes } from '@/routes'
import { useApolloClient } from '@apollo/client'
import { UserProfileContext } from '@view/providers/UserProfileProvider'
import { firebaseEventListener } from '@/events/firebaseEventListener'
import { refreshUserToken } from '@/firebaseConfig'
import { eventBus } from '@/events/eventBus'
import { closeSnackbar, SnackbarKey, useSnackbar } from 'notistack'
import { NotificationMessages } from '@interfaces/Notification'
import Button from '@mui/material/Button'
import { NotificationEvent } from '@models/NotificationEvent'
import FirmUser from '@/models/FirmUser'
import BaseUser from '@/models/BaseUser'
import SubscriberUser from '@/models/SubscriberUser'
import log from 'loglevel'

const EventAdapter = (): null => {
  const apolloClient = useApolloClient()
  const { user } = useContext(UserProfileContext)
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const isAttorney = user instanceof FirmUser || user instanceof BaseUser
  const isSubscriber = user instanceof SubscriberUser

  const handleApolloRefetches = () => {
    void apolloClient.refetchQueries({ include: 'active' }).then(() => {
      log.warn('refetched Queries...')
    })
  }

  const handleRefreshUserToken = () => {
    void refreshUserToken().then(() => log.info('User claims updated'))
  }

  const createLegalMatterAction =
    (legalMatterKey: string) => (snackbarId: SnackbarKey) => (
      <>
        <Button
          sx={{ margin: '0px 4px' }}
          color="secondary"
          variant="outlined"
          onClick={() => {
            navigate(routes.legalMatterDetail.param({ legalMatterKey }))
          }}
        >
          Go to Legal Matter
        </Button>
        <Button
          sx={{ margin: '0px 4px' }}
          color="error"
          variant="contained"
          onClick={() => {
            closeSnackbar(snackbarId)
          }}
        >
          Dismiss
        </Button>
      </>
    )

  const handleGlobalEventsForSubscribers = (eventData: NotificationEvent) => {
    const action = createLegalMatterAction(eventData.legal_matter_key)

    void enqueueSnackbar(
      NotificationMessages[eventData.type]?.subscriber_message,
      {
        variant: 'info',
        action,
      },
    )

    void apolloClient.cache.evict({ fieldName: 'legalMatters' })
    void apolloClient.cache.gc()
  }

  const handleGlobalEventsForAttorneys = (eventData: NotificationEvent) => {
    const action = createLegalMatterAction(eventData.legal_matter_key)

    if (eventData.event.identity_encoded != user.key) {
      void enqueueSnackbar(
        NotificationMessages[eventData.type]?.attorney_message,
        {
          variant: 'info',
          action,
        },
      )
    }

    void apolloClient.cache.evict({ fieldName: 'legalMatters' })
    void apolloClient.cache.gc()
  }

  const handleEventsForClaimersAndAssignerAttorneys = (
    eventData: NotificationEvent,
  ) => {
    const action = createLegalMatterAction(eventData.legal_matter_key)

    if (
      user.roles.includes('legalMatters.assigner') ||
      user.roles.includes('legalMatters.claimer')
    ) {
      void enqueueSnackbar(
        NotificationMessages[eventData.type]?.attorney_message,
        {
          variant: 'info',
          action,
        },
      )
    }
    void apolloClient.cache.evict({ fieldName: 'legalMatters' })
    void apolloClient.cache.gc()
  }

  const handleEventsForAssignerAttorneys = (eventData: NotificationEvent) => {
    const action = createLegalMatterAction(eventData.legal_matter_key)

    if (
      user.roles.includes('legalMatters.assigner') &&
      user.key != eventData.event.legal_matter?.assigned_lawyer_ref_encoded
    ) {
      void enqueueSnackbar(
        NotificationMessages[eventData.type]?.attorney_message,
        {
          variant: 'info',
          action,
        },
      )
    }

    void apolloClient.cache.evict({ fieldName: 'legalMatters' })
    void apolloClient.cache.gc()
  }

  const handleAttorneyLegalMatterAssigned = (eventData: NotificationEvent) => {
    const action = createLegalMatterAction(eventData.legal_matter_key)

    if (eventData.event.legal_matter?.assigned_lawyer_ref_encoded == user.key) {
      void enqueueSnackbar(
        NotificationMessages[eventData.type]?.attorney_message,
        {
          variant: 'info',
          action,
        },
      )
    }
    void apolloClient.cache.evict({ fieldName: 'legalMatters' })
    void apolloClient.cache.gc()
  }

  const handleAttorneyLegalMatterShareRejected = (
    eventData: NotificationEvent,
  ) => {
    const action = createLegalMatterAction(eventData.legal_matter_key)

    if (user.key == eventData.event.legal_matter?.assigned_lawyer_ref_encoded) {
      void enqueueSnackbar(
        NotificationMessages[eventData.type]?.attorney_message,
        {
          variant: 'info',
          action,
        },
      )
    }

    void apolloClient.cache.evict({ fieldName: 'legalMatters' })
    void apolloClient.cache.gc()
  }

  useEffect(() => {
    if (!user.profile?.aclIdentities) return

    const unsubscribe = firebaseEventListener(user.profile.aclIdentities)

    eventBus.on('*', handleApolloRefetches)
    eventBus.on('UserClaimsUpdated', handleRefreshUserToken)

    if (isAttorney) {
      eventBus.on(
        'LegalMatterCreated',
        handleEventsForClaimersAndAssignerAttorneys,
      )
      eventBus.on(
        'LegalMatterAssignedToFirm',
        handleEventsForClaimersAndAssignerAttorneys,
      )
      eventBus.on(
        'LegalMatterReferredIn',
        handleEventsForClaimersAndAssignerAttorneys,
      )
      eventBus.on(
        'LegalMatterWithdrawn',
        handleEventsForClaimersAndAssignerAttorneys,
      )
      eventBus.on(
        'LegalMatterReferralAccepted',
        handleEventsForClaimersAndAssignerAttorneys,
      )
      eventBus.on(
        'LegalMatterReferralRejected',
        handleEventsForClaimersAndAssignerAttorneys,
      )

      eventBus.on('LegalMatterAccepted', handleEventsForAssignerAttorneys)
      eventBus.on('LegalMatterRejected', handleEventsForAssignerAttorneys)
      eventBus.on('LegalMatterClaimed', handleEventsForAssignerAttorneys)

      eventBus.on('LegalMatterAssigned', handleAttorneyLegalMatterAssigned)
      eventBus.on(
        'LegalMatterShareRejected',
        handleAttorneyLegalMatterShareRejected,
      )

      GlobalEventsForAttorneys.forEach((event) => {
        eventBus.on(event, handleGlobalEventsForAttorneys)
      })
    }

    if (isSubscriber) {
      GlobalEventsForSubscribers.forEach((event) => {
        eventBus.on(event, handleGlobalEventsForSubscribers)
      })
    }

    return () => {
      eventBus.off('*', handleApolloRefetches)
      eventBus.off('UserClaimsUpdated', handleRefreshUserToken)

      if (isAttorney) {
        eventBus.off(
          'LegalMatterCreated',
          handleEventsForClaimersAndAssignerAttorneys,
        )
        eventBus.off(
          'LegalMatterAssignedToFirm',
          handleEventsForClaimersAndAssignerAttorneys,
        )
        eventBus.off(
          'LegalMatterReferredIn',
          handleEventsForClaimersAndAssignerAttorneys,
        )
        eventBus.off(
          'LegalMatterWithdrawn',
          handleEventsForClaimersAndAssignerAttorneys,
        )
        eventBus.off(
          'LegalMatterReferralAccepted',
          handleEventsForClaimersAndAssignerAttorneys,
        )
        eventBus.off(
          'LegalMatterReferralRejected',
          handleEventsForClaimersAndAssignerAttorneys,
        )

        eventBus.off('LegalMatterAccepted', handleEventsForAssignerAttorneys)
        eventBus.off('LegalMatterRejected', handleEventsForAssignerAttorneys)
        eventBus.off('LegalMatterClaimed', handleEventsForAssignerAttorneys)

        eventBus.off('LegalMatterAssigned', handleAttorneyLegalMatterAssigned)
        eventBus.off(
          'LegalMatterShareRejected',
          handleAttorneyLegalMatterShareRejected,
        )

        GlobalEventsForAttorneys.forEach((event) => {
          eventBus.off(event, handleGlobalEventsForAttorneys)
        })
      }

      if (isSubscriber) {
        GlobalEventsForSubscribers.forEach((event) => {
          eventBus.off(event, handleGlobalEventsForSubscribers)
        })
      }

      unsubscribe()
    }
  }, [apolloClient, user.profile?.aclIdentities])

  return null
}

export default EventAdapter

const GlobalEventsForAttorneys = [
  'LegalMatterClosed',
  'LegalMatterCancelled',
  'LegalMatterShared',
  'LegalMatterCleanExpiredPreviews',
  'QuoteRejected',
  'QuoteAccepted',
]

const GlobalEventsForSubscribers = [
  'LegalMatterAccepted',
  'LegalMatterAssignedToFirm',
  'LegalMatterClaimed',
  'LegalMatterClosed',
  'LegalMatterCancelled',
  'LegalMatterReferredIn',
  'LegalMatterPreviewEnter',
  'LegalMatterReferralAccepted',
  'LegalMatterReferralRejected',
  'QuoteSent',
  'QuoteDeleted',
  'QuoteUpdated',
]
